import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import NoResults from '@Components/NoResults/NoResults';
import SidebarContentWrapper from '@Components/SidebarContentWrapper';
import {
	AnalyticsCardsContainer,
	EnrollmentsCard,
	EnrollmentTypeCard,
	enrollmentIcons,
	FormControlsWrapper,
	StyledIconButton,
	TypeInfo,
	TypeName,
	TypeValue,
} from '@Components/UI/Analytics/Analytics.styled';
import { AnalyticsTotal } from '@Components/UI/Analytics/Assets/Icons/Icons';
import MultipleLinesChart from '@Components/UI/Charts/MultipleLinesChart/MultipleLinesChart';
import DateRangePickerPopup, {
	DateRangeOption,
} from '@Components/UI/Pickers/DateRangePickerPopup/DateRangePickerPopup';
import MonthRangePicker from '@Components/UI/Pickers/MonthRangePicker/MonthRangePicker';
import YearRangePicker from '@Components/UI/Pickers/YearRangePicker/YearRangePicker';
import { WrappedTableCell } from '@Components/UI/Tables/Tables.styled';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import EdUploadFile from '@Components/UI/Inputs/EdUploadFile/EdUploadFile';
import SelectChip from '@Components/UI/Inputs/SelectChip/SelectChip';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { ActionsTableRow } from '@Components/MainTable/MainTable.styled';
import { AnalyticsSlices } from '@Pages/ClassRooms/Constants/Classroom.constants';
import {
	EnrollmentType,
	GraphSlice,
	GraphSliceType,
} from '@Services/classrooms/classrooms.res.types';
import { deepPurple, green, orange, red, yellow } from '@mui/material/colors';
import {
	BreadcrumbContext,
	NavMenuContext,
} from '@Components/UI/Layout/Layout';
import { useTypedSelector } from '@Features/store';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import { TableHeader } from '@Features/generics/generics.type';
import { RouteGroup, SingleRoute } from '@Constants/routes';
import { useSnackbar } from '@Providers/useSnackbar';
import EnrollementModel from '@Pages/ClassRooms/Pages/SingleCourse/Components/EnrollmentModel/EnrollmentModel';
import UnEnrollementModel from '@Pages/ClassRooms/Pages/SingleCourse/Components/EnrollmentModel/UnEnroolmentModel';
import api from '@Services/api';
import { Divider, FlexItem, FlexLayout, GridLayout, Spacer } from '@Styled/utilities';
import { ChipFlexLayout } from '@Pages/ClassRooms/Components/AddEditClassroom.styled';
import {
	Avatar,
	AvatarGroup,
	Button,
	Checkbox,
	Chip,
	Icon,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	SpeedDial,
	SpeedDialAction,
	Switch as MuiSwitch,
} from '@mui/material';
import { TooltipTitle } from '@Utils/TooltipTitle';
import educationBadge, { educationFullNames } from '@Utils/educationBadge';
import AddIcon from '@mui/icons-material/Add';
import AddBoxSharpIcon from '@mui/icons-material/AddBoxSharp';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useParams,
	useRouteMatch,
} from 'react-router-dom';
import styled from 'styled-components';
import { BanButton, StatusChip } from '../Students/StudentTabs/Subscriptions/subscriptions.shared';
import useStandalonePageLoader from '../StandaloneCourses/useStandalonePageLoader';
import StandaloneLoadingProvider from '../StandaloneCourses/StandaloneLoadingProvider';
import ProductEnrollmentInvoices from '@ContentManagement/ProductEnrollmentInvoices';
import useAdminActionPermissions from '../StandaloneCourses/useAdminActionPermissions';

type Instructor = {
	user_id?: number;
	userId?: number;
	user?: { id?: number; first_name?: string; last_name?: string };
};
type MemberCourse = {
	id: number;
	title: string;
	label: string;
	price: number;
	active?: boolean;
	instructor_id?: number;
};
type Bundle = {
	id: number;
	title: string;
	label: string;
	code: string;
	description: string;
	instructor_id: number;
	instructorId?: number;
	category_id: number | null;
	categoryId?: number;
	instructor?: Instructor;
	category?: { id: number; name: string };
	thumbnail: string;
	status: string;
	active: boolean;
	accessible: boolean;
	buyable: boolean;
	featured?: boolean;
	featured_order?: number | null;
	order: number;
	price: number;
	old_price?: number | null;
	payment_methods_allowed: string;
	enrolled_count?: number;
	courses?: MemberCourse[];
	total_courses_price?: number;
	courses_count?: number;
	$extras?: { enrollments_count?: number };
};

type BundleForm = Partial<Bundle> & { standalone_course_ids: number[] };

const TABLE_HEADS: TableHeader[] = [
	{ displayName: 'TITLE', fieldName: 'title', width: '18%' },
	{ displayName: 'INSTRUCTOR', fieldName: 'instructor_id', width: '13%' },
	{ displayName: 'YEAR', fieldName: 'education_years', canSort: false, width: '10%' },
	{ displayName: 'STATUS', fieldName: 'status', width: '8%' },
	{ displayName: 'COURSES', fieldName: 'courses', canSort: false, width: '8%' },
	{ displayName: 'PRICE', fieldName: 'price', width: '8%' },
	{ displayName: 'ENROLLED', fieldName: 'enrolled', canSort: false, width: '8%' },
	{ displayName: 'FEATURED', fieldName: 'featured', canSort: false, width: '13%' },
	{ displayName: 'ACTIVE', fieldName: 'active', canSort: false, width: '7%' },
];

/** A bundle carries no education pivots of its own — its years are the
 *  deduped union of its courses' years (preloaded by the list endpoint). */
const bundleYears = (bundle: any) => {
	const seen = new Map<number, any>();
	(bundle.courses ?? []).forEach((course: any) =>
		(course.educationYears ?? []).forEach((edu: any) => {
			if (edu?.id != null && !seen.has(edu.id)) seen.set(edu.id, edu);
		})
	);
	return Array.from(seen.values());
};

const paymentOptions = [
	{ value: '*', label: 'All' },
	{ value: 'wallet', label: 'Wallet' },
	{ value: 'scratchcard', label: 'Scratchcard' },
];

const apiData = <T,>(response: any): T => response.data.data as T;

const tableCellStyles = {
	fontFamily: 'Roboto',
	fontSize: '1rem',
	padding: '0 1.063rem 0',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	cursor: 'pointer',
} as const;

const Bundles: React.FC = () => {
	const { path, url } = useRouteMatch();
	// bundle authoring screens exist only in the admin portal — deep links
	// bounce back to the list in the instructor one
	const isAdminPortal = api.defaults.baseURL?.includes('/admin/') === true;
	return (
		<StandaloneLoadingProvider>
			<Switch>
				<Route exact path={path} component={BundlesList} />
				<Route
					exact
					path={`${path}/create`}
					render={() => (isAdminPortal ? <BundleFormPage /> : <Redirect to={url} />)}
				/>
				<Route
					exact
					path={`${path}/:label/edit`}
					render={() => (isAdminPortal ? <BundleFormPage /> : <Redirect to={url} />)}
				/>
				<Route exact path={`${path}/:label`} component={BundleManagement} />
				<Route path={`${path}/:label/:page`} component={BundleManagement} />
				<Redirect to={url} />
			</Switch>
		</StandaloneLoadingProvider>
	);
};

const BundlesList: React.FC = () => {
	const history = useHistory();
	const { url } = useRouteMatch();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	// Authoring (create/edit/archive/featured) is admin-only — the instructor
	// portal only operates: the active toggle stays on the operating gate.
	const { can, canAuthor } = useAdminActionPermissions();
	const canCreate = canAuthor('c-cl');
	const canUpdate = can('u-cl');
	const canCurate = canAuthor('u-cl');
	const canDelete = canAuthor('d-cl');
	const [items, setItems] = useState<Bundle[]>([]);
	const [meta, setMeta] = useState<any>({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
	const [filters, setFilters] = useState<any[]>([]);
	const [archiveLabel, setArchiveLabel] = useState<string>();
	const openManagement = useCallback((label: string) => {
		history.push(`${url}/${label}`);
	}, [history, url]);

	const load = useCallback(async (request: Partial<onPageChangeRequestMeta> = {}) => {
		try {
			const from =
				request.startDate && !Number.isNaN(request.startDate.getTime())
					? request.startDate
					: undefined;
			const to =
				request.endDate && !Number.isNaN(request.endDate.getTime())
					? request.endDate
					: undefined;
			await withLoading(async () => {
				const response = await api.post('bundles/all', {
					page: request.activePage || 1,
					perPage: request.perPage || 10,
					query: request.query || '',
					filters: request.filters || [],
					sortBy: request.sortBy || undefined,
					from,
					to,
				});
				setItems(apiData<Bundle[]>(response));
				setMeta(response.data.meta || {});
				setFilters(response.data.filters || []);
			});
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to load bundles');
		}
	}, [displaySnackbar, withLoading]);

	useEffect(() => { load(); }, [load]);

	const updateFeatured = async (bundle: Bundle, changes: { featured?: boolean; featured_order?: number | null }) => {
		try {
			await api.put('bundles/update', { id: bundle.id, ...changes });
			setItems((current) =>
				current.map((item) => (item.id === bundle.id ? { ...item, ...changes } : item))
			);
			if (changes.featured !== undefined) {
				displaySnackbar('success', `Bundle ${changes.featured ? 'featured on' : 'removed from'} landing carousel`);
			}
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to update featured state');
		}
	};

	const updateActive = async (bundle: Bundle, active: boolean) => {
		try {
			await withLoading(() =>
				api.put('bundles/update', { id: bundle.id, active })
			);
			setItems((current) =>
				current.map((item) => (item.id === bundle.id ? { ...item, active } : item))
			);
			displaySnackbar('success', `Bundle ${active ? 'activated' : 'deactivated'} successfully`);
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to update active state');
		}
	};

	const archive = async (close: boolean, confirm?: boolean) => {
		if (confirm && archiveLabel) {
			try {
				await withLoading(() =>
					api.delete('bundles/archive', {
						data: { label: archiveLabel },
					})
				);
				displaySnackbar('success', 'Bundle archived successfully');
				load();
			} catch (error: any) {
				displaySnackbar('error', error.response?.data?.message || 'Unable to archive bundle');
			}
		}
		setArchiveLabel(undefined);
	};

	return (
		<>
			{canCreate && (
				<Actions><Button variant="contained" startIcon={<AddIcon />} onClick={() => history.push(`${url}/create`)}>Add Bundle</Button></Actions>
			)}
			<EdConfirmModal open={Boolean(archiveLabel)} handleClose={archive} text="Are you sure you want to archive this bundle? Existing owners keep their course enrollments." />
			<MainTable
				onPageChangeRequest={load}
				tableFilters={filters}
				totalPages={Number(meta.last_page || 1)}
				total={Number(meta.total || items.length)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={Number(meta.current_page || 1)}
				tableHeads={TABLE_HEADS}
				tableMinWidth="55rem"
				$hasActions
				renderItems={() => items.length === 0 ? (
					<MainTableRow><TableCell colSpan={TABLE_HEADS.length + 1}><NoResults /></TableCell></MainTableRow>
				) : items.map((bundle) => (
					<MainTableRow
						key={bundle.id}
						hover
						sx={{ '&:active': { backgroundColor: '#6c63ff' } }}
					>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(bundle.label)}>{bundle.title}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(bundle.label)}>{`${bundle.instructor?.user?.first_name || ''} ${bundle.instructor?.user?.last_name || ''}`.trim()}</TableCell>
						<TableCell sx={{ ...tableCellStyles, overflow: 'unset' }} onClick={() => openManagement(bundle.label)}>
							<Tooltip title={TooltipTitle(educationFullNames(bundleYears(bundle)))} placement="top">
								<AvatarGroup
									max={2}
									sx={{
										justifyContent: 'flex-end',
										// Pill, not a fixed circle — badge texts must stay inside.
										'& .MuiAvatar-root': {
											width: 'auto',
											minWidth: 22,
											height: 22,
											padding: '0 6px',
											borderRadius: '11px',
											fontSize: '9px',
										},
									}}
								>
									{bundleYears(bundle).map((edu: any) => {
										const badge = educationBadge(edu);
										return (
											<Avatar
												key={edu.id}
												alt="education year"
												sx={{ backgroundColor: badge.color, fontFamily: 'Roboto' }}
											>
												{badge.short}
											</Avatar>
										);
									})}
								</AvatarGroup>
							</Tooltip>
						</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(bundle.label)}>{bundle.status}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(bundle.label)}>{bundle.courses?.length ?? 0}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(bundle.label)}>{Number(bundle.price) === 0 ? 'Free' : bundle.price}</TableCell>
						<TableCell sx={tableCellStyles} onClick={() => openManagement(bundle.label)}>{bundle.$extras?.enrollments_count ?? bundle.enrolled_count ?? 0}</TableCell>
						<TableCell sx={tableCellStyles}>
							<FlexLayout display="flex" alignItems="center" gridGap="0.5rem">
								{canCurate ? (
									<Tooltip placement="top" title={bundle.featured ? 'Remove from landing carousel' : 'Feature on landing carousel'}>
										<MuiSwitch
											size="small"
											checked={Boolean(bundle.featured)}
											onClick={(event) => event.stopPropagation()}
											onChange={(event) => updateFeatured(bundle, { featured: event.target.checked })}
										/>
									</Tooltip>
								) : (
									<MuiSwitch size="small" checked={Boolean(bundle.featured)} disabled />
								)}
								{bundle.featured && (
									<TextField
										size="small"
										type="number"
										disabled={!canCurate}
										inputProps={{ min: 1, style: { width: '3rem', padding: '0.25rem 0.5rem' } }}
										defaultValue={bundle.featured_order ?? ''}
										placeholder="#"
										onClick={(event) => event.stopPropagation()}
										onBlur={(event) => {
											const value = event.target.value === '' ? null : Number(event.target.value);
											if (value !== (bundle.featured_order ?? null)) {
												updateFeatured(bundle, { featured_order: value });
											}
										}}
									/>
								)}
							</FlexLayout>
						</TableCell>
						<TableCell sx={tableCellStyles}>
							{canUpdate ? (
								<Tooltip
									placement="top"
									title={bundle.active ? 'Deactivate' : 'Activate'}
								>
									<Button
										onClick={(event) => {
											event.stopPropagation();
											updateActive(bundle, !bundle.active);
										}}
									>
										{bundle.active ? (
											<ActiveCheckIcon>check</ActiveCheckIcon>
										) : (
											<InactiveCloseIcon>close</InactiveCloseIcon>
										)}
									</Button>
								</Tooltip>
							) : bundle.active ? (
								<ActiveCheckIcon>check</ActiveCheckIcon>
							) : (
								<InactiveCloseIcon>close</InactiveCloseIcon>
							)}
						</TableCell>
						<ActionsTableRow
							sx={{
							padding: '0 1.063rem 0',
							position: 'sticky',
							right: 0,
							background: 'linear-gradient(260deg, white, white, transparent)',
						}}
						>
							{(canCurate || canDelete) && (
								<SpeedDial
									FabProps={{
										disableRipple: true,
										disableTouchRipple: true,
										focusRipple: false,
										disableFocusRipple: true,
										style: {
											borderRadius: 0,
											boxShadow: '0 0 0 0',
											backgroundColor: 'transparent',
											color: '#000',
										},
									}}
									direction="left"
									ariaLabel="Bundle actions"
									icon={<MoreVertIcon />}
								>
									{canDelete && (
										<SpeedDialAction
											key="archive"
											icon={<StyledDeleteIcon />}
											tooltipTitle="Delete"
											onClick={(event) => {
												event.stopPropagation();
												setArchiveLabel(bundle.label);
											}}
										/>
									)}
									{canCurate && (
										<SpeedDialAction
											key="edit"
											icon={<StyledEditIcon />}
											tooltipTitle="Edit"
											onClick={(event) => {
												event.stopPropagation();
												history.push(`${url}/${bundle.label}/edit`);
											}}
										/>
									)}
								</SpeedDial>
							)}
						</ActionsTableRow>
					</MainTableRow>
				))}
			/>
		</>
	);
};

const defaultForm: BundleForm = {
	title: '', label: '', code: '', description: '',
	instructor_id: '' as unknown as number, category_id: '' as unknown as number,
	thumbnail: '', status: 'open',
	active: true, accessible: true, buyable: true, order: 1,
	price: 0, old_price: null, payment_methods_allowed: '*',
	standalone_course_ids: [],
};

const BundleFormPage: React.FC = () => {
	const { label } = useParams<{ label?: string }>();
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const { dropdownInstructors, dropdownCategories } = useTypedSelector((state) => state.dropdowns);
	const isAdminPortal = api.defaults.baseURL?.includes('/admin/');
	const { control, handleSubmit, reset, watch, setValue, formState: { isSubmitting } } = useForm<BundleForm>({ defaultValues: defaultForm });
	const [loadedBundle, setLoadedBundle] = useState<Bundle>();
	const [availableCourses, setAvailableCourses] = useState<MemberCourse[]>([]);
	const hydratedBundleLabel = useRef<string>();
	const selectedCourses = watch('standalone_course_ids') || [];
	const featured = Boolean(watch('featured'));
	const price = Number(watch('price') || 0);
	const instructorId = watch('instructor_id');

	useEffect(() => {
		if (!label) return;
		withLoading(async () => {
			const response = await api.post('bundles', { label });
			hydratedBundleLabel.current = undefined;
			setLoadedBundle(apiData<Bundle>(response));
		}).catch((error) => {
			displaySnackbar('error', error.response?.data?.message || 'Unable to load bundle');
		});
	}, [displaySnackbar, label, withLoading]);

	useEffect(() => {
		if (!label || !loadedBundle || hydratedBundleLabel.current === label) return;
		if (isAdminPortal && dropdownInstructors.length === 0) return;

		const instructorName = loadedBundle.instructor?.user;
		const loadedInstructorId = loadedBundle.instructor_id
			?? loadedBundle.instructorId
			?? loadedBundle.instructor?.user_id
			?? loadedBundle.instructor?.userId
			?? instructorName?.id
			?? dropdownInstructors.find((item: any) =>
				item.first_name === instructorName?.first_name
				&& item.last_name === instructorName?.last_name
			)?.id;
		const categoryId = loadedBundle.category_id
			?? loadedBundle.categoryId
			?? loadedBundle.category?.id
			?? dropdownCategories.find((item: any) =>
				item.name === loadedBundle.category?.name
			)?.id;

		reset({
			...defaultForm,
			...loadedBundle,
			instructor_id: loadedInstructorId === undefined ? defaultForm.instructor_id : Number(loadedInstructorId),
			category_id: categoryId === undefined || categoryId === null ? defaultForm.category_id : Number(categoryId),
			standalone_course_ids: loadedBundle.courses?.map((course) => course.id) || [],
		});
		hydratedBundleLabel.current = label;
	}, [
		dropdownCategories,
		dropdownInstructors,
		isAdminPortal,
		label,
		loadedBundle,
		reset,
	]);

	// The member-course picker lists the owning instructor's live standalone
	// courses (admin picks the instructor first; instructor is auto-scoped).
	useEffect(() => {
		if (isAdminPortal && !instructorId) {
			setAvailableCourses([]);
			return;
		}
		const request = async () => {
			try {
				const response = await api.post('standalone-courses/all', {
					page: 1,
					perPage: 500,
					query: '',
					filters: isAdminPortal ? [{ instructor_id: Number(instructorId) }] : [],
				});
				setAvailableCourses(apiData<MemberCourse[]>(response));
			} catch (error: any) {
				displaySnackbar('error', error.response?.data?.message || 'Unable to load standalone courses');
			}
		};
		request();
	}, [displaySnackbar, instructorId, isAdminPortal]);

	// Deselect member courses that belong to a previously selected instructor.
	useEffect(() => {
		if (!isAdminPortal || !availableCourses.length) return;
		const availableIds = new Set(availableCourses.map((course) => course.id));
		const filtered = selectedCourses.filter((id) => availableIds.has(id));
		if (filtered.length !== selectedCourses.length) {
			setValue('standalone_course_ids', filtered);
		}
	}, [availableCourses, isAdminPortal, selectedCourses, setValue]);

	const coursesById = useMemo(() => {
		const map = new Map<number, MemberCourse>();
		availableCourses.forEach((course) => map.set(course.id, course));
		(loadedBundle?.courses || []).forEach((course) => {
			if (!map.has(course.id)) map.set(course.id, course);
		});
		return map;
	}, [availableCourses, loadedBundle]);
	const totalCoursesPrice = useMemo(
		() => selectedCourses.reduce((total, id) => total + Number(coursesById.get(id)?.price || 0), 0),
		[coursesById, selectedCourses]
	);

	const courseDisplay = (id: number) => {
		const course = coursesById.get(id);
		return course ? `${course.title} (${course.label})` : undefined;
	};

	const removeCourse = (value: number) => {
		setValue('standalone_course_ids', selectedCourses.filter((id) => id !== value), {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const save = async (values: BundleForm) => {
		if (Number(values.old_price || 0) > 0 && Number(values.old_price) <= Number(values.price || 0)) {
			displaySnackbar('error', 'Old price must be greater than price');
			return;
		}
		if ((values.standalone_course_ids || []).length < 2) {
			displaySnackbar('error', 'A bundle requires at least two standalone courses');
			return;
		}
		try {
			const payload: any = {
				...values,
				category_id: values.category_id === ('' as unknown as number) ? null : Number(values.category_id),
			};
			if (isAdminPortal) payload.instructor_id = Number(values.instructor_id);
			if (price === 0) payload.old_price = null;
			if (label && values.featured === false) payload.featured_order = null;
			const response = await withLoading(() =>
				label
					? api.put('bundles/update', { ...payload, id: values.id })
					: api.post('bundles/create', payload)
			);
			const bundle = apiData<Bundle>(response);
			displaySnackbar('success', `Bundle ${label ? 'updated' : 'created'} successfully`);
			history.push(`/dashboard/classrooms/bundles/${bundle.label || values.label}`);
		} catch (error: any) {
			displaySnackbar('error', error.response?.data?.message || 'Unable to save bundle');
		}
	};

	return (
		<BundleFormContainer>
			<FormTitle>{label ? 'Edit' : 'Add'} Bundle</FormTitle>
			<form onSubmit={handleSubmit(save)}>
				<Divider label="Basic Information" />
				<FormGrid>
					<EdFormControl control={control} required name="title" label="Title" />
					{isAdminPortal && <EdFormControl control={control} required select name="instructor_id" label="Instructor">{dropdownInstructors.map((item: any) => <MenuItem key={item.id} value={item.id}>{item.first_name} {item.last_name}</MenuItem>)}</EdFormControl>}
					<EdFormControl control={control} select name="category_id" label="Subject">{dropdownCategories.map((item: any) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</EdFormControl>
					<EdFormControl control={control} required multiline name="description" label="Description" />
				</FormGrid>
				<Divider label="Bundle Identity" />
				<FormGrid>
					<EdFormControl control={control} required name="label" label="Label" />
					<EdFormControl control={control} required name="code" label="Code" />
					<EdFormControl control={control} required select name="status" label="Status">{['open', 'closed', 'soon'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</EdFormControl>
				</FormGrid>
				<Divider label="Courses" />
				<FormGrid>
					<EdFormControl
						control={control}
						required
						select
						multiple
						disabled={isAdminPortal && !instructorId}
						name="standalone_course_ids"
						label="Standalone Courses"
						renderValues={() => (
							<div>
								{selectedCourses.map((id) => (
									<SelectChip
										key={`bundle-course-${id}`}
										label={courseDisplay(id)}
										onDelete={() => removeCourse(id)}
									/>
								))}
							</div>
						)}
						SelectProps={{
							multiple: true,
							renderValue: () => (
								<ChipFlexLayout>
									{selectedCourses.map((id) => courseDisplay(id)).filter(Boolean).join(', ')}
								</ChipFlexLayout>
							),
						}}
					>
						{availableCourses.map((course) => (
							<MenuItem key={course.id} value={course.id}>
								<Checkbox checked={selectedCourses.includes(course.id)} />
								{course.title} ({course.label}) — {Number(course.price) === 0 ? 'Free' : course.price}
							</MenuItem>
						))}
					</EdFormControl>
					<PriceSummary>
						{isAdminPortal && !instructorId ? (
							<Typography color="error">Select an instructor first to choose their courses.</Typography>
						) : (
							<Typography color="textSecondary">
								{selectedCourses.length} course{selectedCourses.length === 1 ? '' : 's'} selected — total individual price: <b>{totalCoursesPrice}</b>
							</Typography>
						)}
						{selectedCourses.length >= 2 && price >= totalCoursesPrice && totalCoursesPrice > 0 && (
							<Typography color="error">
								Bundle price is not lower than the sum of the individual course prices.
							</Typography>
						)}
					</PriceSummary>
				</FormGrid>
				<Divider label="Commerce & Availability" />
				<FormGrid>
					<EdFormControl control={control} required type="number" name="order" label="Order" />
					{label && (
						<EdFormControl
							control={control}
							type="number"
							name="featured_order"
							label="Featured Order"
							disabled={!featured}
						/>
					)}
					<EdFormControl control={control} required type="number" name="price" label="Price" />
					<EdFormControl control={control} type="number" disabled={price === 0} name="old_price" label="Price before discount" />
					<EdFormControl control={control} required select name="payment_methods_allowed" label="Payment">{paymentOptions.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}</EdFormControl>
				</FormGrid>
				<SwitchGrid>
					<EdFormControl control={control} switch name="active" label="Active" />
					<EdFormControl control={control} switch name="accessible" label="Accessible" />
					<EdFormControl control={control} switch name="buyable" label="Buyable" />
					{label && <EdFormControl control={control} switch name="featured" label="Featured" />}
				</SwitchGrid>
				<Divider label="Cover & Media" />
				<CoverGrid>
					<EdUploadFile
						folder="classrooms"
						uploadLabel="UPLOAD"
						uploadIcon={<Icon>add</Icon>}
						control={control}
						name="thumbnail"
						label="Cover"
						optimize={false}
						onUploadError={(error: any) => displaySnackbar(
							'error',
							error?.response?.data?.message || error?.message || 'Unable to upload cover'
						)}
					/>
				</CoverGrid>
				<Actions><Button type="button" onClick={() => history.goBack()}>Cancel</Button><Button disabled={isSubmitting} type="submit" variant="contained">Save</Button></Actions>
			</form>
		</BundleFormContainer>
	);
};

const managementRoute = (
	title: string,
	route: string,
	code: string
): SingleRoute => ({ title, route, code, component: null });

const BundleManagement: React.FC = () => {
	const { label, page } = useParams<{ label: string; page?: string }>();
	const [bundle, setBundle] = useState<Bundle>();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const { setCurrentRouteGroup, setBaseURL } = useContext(NavMenuContext);
	const { addLink, removeLink } = useContext(BreadcrumbContext);
	const isAdminPortal = api.defaults.baseURL?.includes('/admin/');
	const managementRouteGroup = useMemo<RouteGroup>(() => ({
		title: 'Bundle',
		route: 'bundles/:label',
		code: '',
		component: null,
		isRouted: true,
		isParent: true,
		overviewRoute: 'overview',
		routes: isAdminPortal
			? [
				managementRoute('Users', 'users', 'v-enrl-cl'),
				managementRoute('Analytics', 'analytics', 'v-anlt-cl'),
				managementRoute('Invoices', 'invoices', 'v-anlt-cl'),
			]
			: [
				managementRoute('Users', 'users', 'classroom_tab'),
				managementRoute('Analytics', 'analytics', 'v-anlt-cl'),
				managementRoute('Invoices', 'invoices', 'v-anlt-cl'),
			],
	}), [isAdminPortal]);
	const managementPages = useMemo(
		() => ['overview', ...managementRouteGroup.routes.map((route) => route.route)],
		[managementRouteGroup]
	);
	const activePage = page || 'overview';
	const load = useCallback(async () => {
		try {
			await withLoading(async () => {
				const response = await api.post('bundles', { label });
				setBundle(apiData<Bundle>(response));
			});
		}
		catch (error: any) { displaySnackbar('error', error.response?.data?.message || 'Unable to load bundle'); }
	}, [displaySnackbar, label, withLoading]);
	useEffect(() => { load(); }, [load]);
	useEffect(() => {
		setCurrentRouteGroup?.(managementRouteGroup);
		setBaseURL?.(`/dashboard/classrooms/bundles/${label}`);
	}, [label, managementRouteGroup, setBaseURL, setCurrentRouteGroup]);
	useEffect(() => {
		if (!bundle) return;
		addLink?.({
			route: `/dashboard/classrooms/bundles/${label}`,
			title: bundle.title,
			routeHash: 'bundle',
		});
		return () => removeLink?.('bundle');
	}, [addLink, bundle, label, removeLink]);
	if (!managementPages.includes(activePage)) return <Redirect to={`/dashboard/classrooms/bundles/${label}`} />;
	return (
		<>
			{bundle && <ManagementTitle>{bundle.title}</ManagementTitle>}
			{bundle && activePage === 'overview' && <BundleOverview bundle={bundle} />}
			{bundle && activePage === 'users' && <BundleUsers bundleId={bundle.id} price={Number(bundle.price || 0)} />}
			{bundle && activePage === 'analytics' && <BundleAnalytics bundle={bundle} />}
			{bundle && activePage === 'invoices' && <BundleInvoices bundle={bundle} />}
		</>
	);
};

const BundleOverview: React.FC<{ bundle: Bundle }> = ({ bundle }) => {
	const history = useHistory();
	const courses = bundle.courses || [];
	const totalPrice = bundle.total_courses_price
		?? courses.reduce((total, course) => total + Number(course.price || 0), 0);
	return (
		<FormPaper>
			<Typography variant="h6">Included Courses</Typography>
			<Typography color="textSecondary" sx={{ marginBottom: 2 }}>
				Bundle price: <b>{Number(bundle.price) === 0 ? 'Free' : bundle.price}</b>
				{' — '}total individual price: <b>{totalPrice}</b>
			</Typography>
			{courses.length === 0 ? (
				<Typography>No active courses in this bundle. It is hidden from students.</Typography>
			) : (
				<Table sx={{ minWidth: '40rem' }}>
					<TableHead>
						<TableRow>
							<TableCell>Course</TableCell>
							<TableCell>Label</TableCell>
							<TableCell>Price</TableCell>
							<TableCell>Active</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{courses.map((course) => (
							<TableRow
								key={course.id}
								hover
								sx={{ cursor: 'pointer' }}
								onClick={() => history.push(`/dashboard/classrooms/standalone-courses/${course.label}`)}
							>
								<TableCell>{course.title}</TableCell>
								<TableCell>{course.label}</TableCell>
								<TableCell>{Number(course.price) === 0 ? 'Free' : course.price}</TableCell>
								<TableCell>{course.active === false ? 'Inactive' : 'Active'}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</FormPaper>
	);
};

const USER_TABLE_HEADS: TableHeader[] = [
	{ displayName: 'USERNAME', fieldName: 'username', width: '12%' },
	{ displayName: 'FIRST NAME', fieldName: 'first_name', width: '12%' },
	{ displayName: 'LAST NAME', fieldName: 'last_name', width: '12%' },
	{ displayName: 'PHONE', fieldName: 'phone_number', width: '13%' },
	{ displayName: 'METHOD', fieldName: 'purchase_method', width: '10%' },
	{ displayName: 'ENROLLED ON', fieldName: 'enrolled_on', width: '13%' },
	{ displayName: 'STATUS', fieldName: 'status', canSort: false, width: '10%' },
];

const USER_FILTERS = [
	{
		name: 'Enrollment Status',
		value: 'enrolled',
		type: 'dropdown',
		options: [
			{ name: 'Enrolled', value: true },
			{ name: 'Not Enrolled', value: false },
		],
	},
	{
		name: 'Method',
		value: 'purchase_method',
		type: 'dropdown',
		options: ['admin', 'instructor', 'wallet', 'scratchcard', 'free'].map(
			(value) => ({ name: value, value })
		),
	},
];

const errorMessage = (error: any, fallback: string) =>
	error.response?.data?.message || error.data?.message || fallback;

const BundleUsers: React.FC<{ bundleId: number; price: number }> = ({ bundleId, price }) => {
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const isAdmin = api.defaults.baseURL?.includes('/admin/') === true;
	const { can } = useAdminActionPermissions();
	const canManage = can('u-std-mng-cr');
	const [users, setUsers] = useState<any[]>([]);
	const [meta, setMeta] = useState<any>({});
	const [lastRequest, setLastRequest] = useState<Partial<onPageChangeRequestMeta>>({});
	const [selectedUser, setSelectedUser] = useState<any>();
	const [openEnroll, setOpenEnroll] = useState(false);
	const [openUnenroll, setOpenUnenroll] = useState(false);
	const [deduct, setDeduct] = useState<boolean | undefined>();
	const [refund, setRefund] = useState<boolean | undefined>();

	const load = useCallback(
		async (request: Partial<onPageChangeRequestMeta> = {}) => {
			setLastRequest(request);
			try {
				await withLoading(async () => {
					const response = await api.post('bundles/users', {
						bundle_id: bundleId,
						page: request.activePage || 1,
						perPage: request.perPage || 10,
						filters: request.filters || [],
						query: request.query || '',
						// no default: unsorted loads use the backend's roster
						// ordering (enrolled first, newest enrollment first)
						sortBy: request.sortBy,
					});
					setUsers(apiData<any[]>(response));
					setMeta(response.data.meta || {});
				});
			} catch (error: any) {
				displaySnackbar('error', errorMessage(error, 'Unable to load bundle users'));
			}
		},
		[bundleId, displaySnackbar, withLoading]
	);

	useEffect(() => {
		load();
	}, [load]);

	const closeEnroll = () => {
		setOpenEnroll(false);
		setSelectedUser(undefined);
		setDeduct(undefined);
	};

	const closeUnenroll = () => {
		setOpenUnenroll(false);
		setSelectedUser(undefined);
		setRefund(undefined);
	};

	const enroll = async () => {
		if (!selectedUser) return;
		try {
			await withLoading(() =>
				api.post('bundles/users/enroll', {
					bundle_id: bundleId,
					user_id: selectedUser.id,
					...(isAdmin && price > 0 ? { deduct } : {}),
				})
			);
			displaySnackbar('success', 'Student enrolled successfully');
			closeEnroll();
			load(lastRequest);
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, 'Unable to enroll student'));
		}
	};

	const unenroll = async () => {
		if (!selectedUser || !isAdmin) return;
		try {
			await withLoading(() =>
				api.delete('bundles/users/remove', {
					data: {
						bundle_id: bundleId,
						user_id: selectedUser.id,
						refund: Boolean(refund),
					},
				})
			);
			displaySnackbar(
				'success',
				refund
					? 'Enrollment removed and refund processed successfully'
					: 'Enrollment removed successfully'
			);
			closeUnenroll();
			load(lastRequest);
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, 'Unable to remove enrollment'));
		}
	};

	// Available to BOTH portals: the instructor route /bundles/users/status is
	// owner-scoped server-side. Unenroll/refund stay admin-only below.
	const setStatus = async (user: any, active: boolean) => {
		try {
			await withLoading(() =>
				api.post('bundles/users/status', {
					bundle_id: bundleId,
					user_id: user.id,
					active,
				})
			);
			displaySnackbar('success', active ? 'Student unbanned' : 'Student banned — the student loses access to the bundle courses');
			load(lastRequest);
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, 'Unable to update enrollment status'));
		}
	};

	return (
		<SidebarContentWrapper>
			<EnrollementModel
				setDeduct={setDeduct}
				handleClose={closeEnroll}
				openEnrolled={openEnroll}
				onEnroll={enroll}
				showDeductChoices={isAdmin && price > 0}
				modelText="student"
				confirmationText={
					isAdmin && price > 0
						? 'Are you sure you want to enroll this student in this bundle? The student will be enrolled in every bundle course they do not already own.'
						: isAdmin
						? 'Are you sure you want to enroll this student in this free bundle? The student will be enrolled in every bundle course they do not already own. No wallet deduction or invoice will be created.'
						: 'Are you sure you want to assign this bundle to this student? The student will be enrolled in every bundle course they do not already own. No wallet deduction or invoice will be created.'
				}
			/>
			{isAdmin && (
				<UnEnrollementModel
					handleCloseUnEnrolledModel={closeUnenroll}
					onUNEnroll={unenroll}
					setRefund={setRefund}
					openUnEnrolled={openUnenroll}
					modelText="student"
					confirmationText={`Are you sure you want to unenroll this student from this bundle? The refundable historical amount is ${
						selectedUser?.refundable_amount || 0
					}. Course enrollments created by this bundle will also be removed; individually purchased courses are kept.`}
				/>
			)}

			<MainTable
				onPageChangeRequest={load}
				tableFilters={USER_FILTERS}
				totalPages={Number(meta.last_page || 1)}
				total={Number(meta.total || users.length)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={Number(meta.current_page || 1)}
				tableHeads={USER_TABLE_HEADS}
				tableMinWidth={650}
				renderItems={() =>
					users.length === 0 ? (
						<MainTableRow>
							<TableCell colSpan={USER_TABLE_HEADS.length}>
								<NoResults />
							</TableCell>
						</MainTableRow>
					) : (
						users.map((user) => (
							<MainTableRow key={user.id} hover sx={{ height: '52px' }}>
								<Cell>
									<Chip label={user.username || '-'} sx={{ fontSize: '13px' }} />
								</Cell>
								<Cell>{user.first_name || '-'}</Cell>
								<Cell>{user.last_name || '-'}</Cell>
								<Tooltip title={user.phone_number || '-'} placement="top">
									<Cell>{user.phone_number || '-'}</Cell>
								</Tooltip>
								<Cell>{user.purchase_method || '-'}</Cell>
								<Cell>
									{user.enrolled_on ? (
										<DateTimeFormatter date={user.enrolled_on} />
									) : (
										'-'
									)}
								</Cell>
								<Cell>
									{user.enrolled || user.suspended ? (
										<FlexLayout alignItems="center">
											{(!isAdmin || !canManage) && (
												<StatusChip status={user.suspended ? 'banned' : 'active'} />
											)}
											{canManage &&
												(user.suspended ? (
													<Tooltip title="Unban Student" placement="left">
														<BanButton banned onClick={() => setStatus(user, true)} />
													</Tooltip>
												) : (
													<Tooltip title="Ban Student" placement="left">
														<BanButton banned={false} onClick={() => setStatus(user, false)} />
													</Tooltip>
												))}
											{isAdmin && canManage && (
												<Tooltip title="Unenroll Student" placement="left">
													<Button
														onClick={() => {
															setSelectedUser(user);
															setOpenUnenroll(true);
														}}
													>
														<IndeterminateCheckBoxOutlinedIcon
															sx={{ color: '#5ac0fc', height: 27, width: 26 }}
														/>
													</Button>
												</Tooltip>
											)}
										</FlexLayout>
									) : canManage ? (
										<Tooltip title="Enroll Student" placement="left">
											<Button
												onClick={() => {
													setSelectedUser(user);
													setOpenEnroll(true);
												}}
											>
												<AddBoxSharpIcon
													sx={{ color: '#5ac0fc', height: 26, width: 26 }}
												/>
											</Button>
										</Tooltip>
									) : (
										'-'
									)}
								</Cell>
							</MainTableRow>
						))
					)
				}
			/>
		</SidebarContentWrapper>
	);
};

type BundleAnalyticsData = {
	enrollment_summary?: {
		purchase_methods_total: number;
		purchase_methods: Array<{
			purchase_method: EnrollmentType;
			purchase_method_count: number;
		}>;
	};
	member_courses?: any[];
	graph?: GraphSlice[];
};

type BundleAnalyticsFormValues = {
	slice: GraphSliceType;
	dates: Date[];
};

const MEMBER_COURSES_TABLE_HEADS: TableHeader[] = [
	{ displayName: 'Course', fieldName: 'title', canSort: false, width: '35%' },
	{ displayName: 'Price', fieldName: 'price', canSort: false },
	{ displayName: 'Active', fieldName: 'active', canSort: false },
	{ displayName: 'Enrolled Via Bundle', fieldName: 'enrolled_via_bundle', canSort: false },
];

const BundleAnalytics: React.FC<{ bundle: Bundle }> = ({ bundle }) => {
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const [data, setData] = useState<BundleAnalyticsData>({});
	const [dateRange, setDateRange] = useState<DateRangeOption>('last 2 weeks');
	const [dateOpen, setDateOpen] = useState(false);
	const [dateAnchor, setDateAnchor] = useState<HTMLElement>();
	const defaults = useMemo<BundleAnalyticsFormValues>(() => {
		const start = new Date();
		start.setDate(start.getDate() - 14);
		return { slice: 'day', dates: [start, new Date()] };
	}, []);
	const { control, watch, setValue, reset, getValues } = useForm<BundleAnalyticsFormValues>({
		defaultValues: defaults,
	});
	const selectedSlice = watch('slice');

	const load = useCallback(
		async (form?: BundleAnalyticsFormValues) => {
			const values = form || getValues();
			try {
				await withLoading(async () => {
					const response = await api.post('bundles/analytics', {
						bundle_id: bundle.id,
						page: 1,
						perPage: 10,
						from: values.dates[0],
						to: values.dates[1],
						slice: values.slice,
					});
					setData(response.data.data || {});
				});
			} catch (error: any) {
				displaySnackbar('error', error.response?.data?.message || 'Unable to load bundle analytics');
			}
		},
		[bundle.id, displaySnackbar, getValues, withLoading]
	);

	// Fetch once per bundle — deliberately NOT keyed on `load`: its identity
	// changes when the snackbar/loading providers re-render, which would
	// re-trigger this effect after every failed request (request loop).
	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bundle.id]);

	// MainTable's Pagination re-fires onPageChangeRequest whenever the callback
	// identity changes (Pagination.tsx effect deps) — an inline arrow here loops
	// forever: request → setData → render → new arrow → request. Must be stable.
	const tableRequest = useCallback(() => load(), [load]);

	const pickTypeColor = (type: EnrollmentType) => {
		if (type === 'admin') return red[500];
		if (type === 'wallet') return deepPurple[500];
		if (type === 'scratchcard') return orange[500];
		if (type === 'free') return yellow[600];
		return green.A700;
	};

	const changeDateRange = (type: DateRangeOption) => {
		setDateRange(type);
		if (type === 'custom') return;
		const start = new Date();
		const end = new Date();
		if (type === 'last 2 weeks') start.setDate(start.getDate() - 14);
		if (type === 'last month') start.setMonth(start.getMonth() - 1);
		if (type === 'last 3 months') start.setMonth(start.getMonth() - 2);
		setValue('dates', [start, end]);
	};

	const resetFilters = () => {
		reset(defaults);
		setDateRange('last 2 weeks');
		load(defaults);
	};

	return (
		<BundleAnalyticsPage>
			{data.enrollment_summary && (
				<ResponsiveAnalyticsCards>
					<EnrollmentsCard elevation={10}>
						<AnalyticsTotal />
						<TypeInfo>
							<span>{data.enrollment_summary.purchase_methods_total}</span>
							<label>Enrollments</label>
						</TypeInfo>
					</EnrollmentsCard>
					{data.enrollment_summary.purchase_methods.map((method) => (
						<EnrollmentTypeCard
							key={method.purchase_method}
							color={pickTypeColor(method.purchase_method)}
						>
							<TypeInfo>
								{enrollmentIcons[method.purchase_method]}
								<TypeValue>{method.purchase_method_count}</TypeValue>
							</TypeInfo>
							<TypeName>{method.purchase_method}</TypeName>
						</EnrollmentTypeCard>
					))}
				</ResponsiveAnalyticsCards>
			)}

			<MainTable
				tableFilters={[]}
				RowsperPage={data.member_courses?.length || 10}
				onPageChangeRequest={tableRequest}
				totalPages={1}
				total={data.member_courses?.length || 0}
				pageNum={1}
				tableHeads={MEMBER_COURSES_TABLE_HEADS}
				renderItems={() => {
					if (!data.member_courses?.length) {
						return (
							<TableRow>
								<TableCell colSpan={MEMBER_COURSES_TABLE_HEADS.length}>
									<NoResults />
								</TableCell>
							</TableRow>
						);
					}
					return data.member_courses.map((course: any) => (
						<TableRow
							key={course.id}
							hover
							sx={{ cursor: 'pointer' }}
							onClick={() => history.push(`/dashboard/classrooms/standalone-courses/${course.label}/analytics`)}
						>
							<WrappedTableCell align="center">{course.title}</WrappedTableCell>
							<TableCell align="center">{Number(course.price) === 0 ? 'Free' : course.price}</TableCell>
							<TableCell align="center">{course.active ? 'Active' : 'Inactive'}</TableCell>
							<TableCell align="center">{course.enrolled_via_bundle}</TableCell>
						</TableRow>
					));
				}}
			/>

			<FormControlsWrapper>
				<EdFormControl select name="slice" control={control}>
					{AnalyticsSlices.map((slice) => (
						<MenuItem key={slice.value} value={slice.value}>
							{slice.label}
						</MenuItem>
					))}
				</EdFormControl>
				<Spacer mx={14} />
				<StyledIconButton
					onClick={(event) => {
						setDateOpen(!dateOpen);
						setDateAnchor(event.currentTarget);
					}}
				>
					<Icon>event</Icon>
				</StyledIconButton>
				<DateRangePickerPopup
					open={dateOpen}
					anchorEl={dateAnchor}
					onClose={() => setDateOpen(false)}
					onChange={changeDateRange}
					rangeOptionValue={dateRange}
				>
					<DateRangePickerPopup.Body>
						{selectedSlice === 'month' && (
							<MonthRangePicker control={control as any} name="dates" />
						)}
						{selectedSlice === 'year' && (
							<YearRangePicker control={control as any} name="dates" />
						)}
					</DateRangePickerPopup.Body>
				</DateRangePickerPopup>
			</FormControlsWrapper>
			<FlexLayout alignItems="flex-end">
				<FlexItem flex="1" />
				<FlexLayout>
					<Button onClick={resetFilters} variant="outlined">
						Reset
					</Button>
					<Spacer mx="1rem" />
					<Button onClick={() => load()} variant="contained">
						Apply
					</Button>
				</FlexLayout>
			</FlexLayout>

			<MultipleLinesChart
				sliceType={selectedSlice}
				colors={() => '#6C63FF'}
				data={data.graph || []}
			/>
		</BundleAnalyticsPage>
	);
};

/**
 * Invoices subtab — the classroom EnrollmentInvoices price-group design
 * (summary money cards + method buckets incl. the derived promocode split).
 * Replaced the flat per-invoice table; the old `bundles/invoices` endpoint
 * stays server-side for deep links.
 */
const BundleInvoices: React.FC<{ bundle: Bundle }> = ({ bundle }) => (
	<ProductEnrollmentInvoices scope="bundle" productId={bundle.id} />
);

const Actions = styled.div`display: flex; justify-content: flex-end; gap: 1rem; margin: 1rem 0 2rem;`;
const FormPaper = styled(Paper)`padding: 2rem; overflow-x: auto;`;
const BundleFormContainer = styled.div`
	padding: 1.375rem;
	border-radius: 10px;
	border: 1px solid ${(props) => props.theme.palette.primary.main};
	background: #fff;
`;
const FormTitle = styled.h1`
	margin: 0 0 0.25rem;
	font-size: 1.125rem;
	font-weight: 700;
	line-height: 1.5;
`;
const FormGrid = styled(GridLayout)`grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 2rem; margin: 1.5rem 0; @media (max-width: 760px) { grid-template-columns: 1fr; }`;
const CoverGrid = styled(GridLayout)`
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 2rem;
	margin: 1.5rem 0;
	@media (max-width: 760px) {
		grid-template-columns: 1fr;
	}
`;
const SwitchGrid = styled.div`display: flex; gap: 2rem; flex-wrap: wrap; margin: 1.5rem 0;`;
const PriceSummary = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 0.5rem;
`;
const ManagementTitle = styled.h2`
	font-size: 1.25rem;
	font-weight: bold;
	margin: 2.875rem 0 2.313rem 2.125rem;
`;
const BundleAnalyticsPage = styled.div`
	width: 100%;
	max-width: 100%;
	min-width: 0;
`;
const ResponsiveAnalyticsCards = styled(AnalyticsCardsContainer)`
	width: 100%;
	flex-wrap: wrap;
	gap: 1rem;

	> * {
		margin: 0;
	}
`;
const Cell = styled(TableCell)`
	font-family: Roboto;
	font-size: 1rem;
	padding: 0 1rem;
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
	max-width: 12rem;
`;
const StyledEditIcon = styled(EditIcon)`
	color: ${(props) => props.theme.palette.darkPurple.main};
	&:hover { color: ${(props) => props.theme.palette.mayaBlue.main}; }
`;
const StyledDeleteIcon = styled(DeleteIcon)`
	color: ${(props) => props.theme.palette.darkPurple.main};
	&:hover { color: ${(props) => props.theme.palette.mayaBlue.main}; }
`;
const StatusIcon = styled(EdIcon)`
	width: 26px;
	height: 26px;
	border-radius: 5px;
	display: flex !important;
	justify-content: center;
	align-items: center;
	line-height: 1.28 !important;
	font-weight: bold;
	font-size: 1.7rem;
`;
const ActiveCheckIcon = styled(StatusIcon)`
	background: #5ac0fc;
	color: #fff;
	border: 2px solid #5ac0fc;
`;
const InactiveCloseIcon = styled(StatusIcon)`
	background: #ff3100;
	color: #fff;
	border: 2px solid #ff3100;
`;

export default Bundles;
